import React, { useState, useEffect } from "react";
import { Eye, Trash2, RotateCcw, User, Image, FileText, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./RegistrationStyles.css";

const url = process.env.REACT_APP_BACKEND_URL;

const DocumentsList = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState({});
  const navigate = useNavigate();

  const fetchDocuments = async () => {
    try {
      const endpoint = showInactive
        ? `${url}/api/documents/all`
        : `${url}/api/documents`;

      const res = await axios.get(endpoint);
      console.log("Documents fetched:", res.data.data);
      setDocuments(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this document?")) {
      try {
        const res = await axios.delete(`${url}/api/documents/${id}`);
        toast.success(res.data.message || "Document deactivated successfully");
        fetchDocuments();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to deactivate document");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Activate this document?")) {
      try {
        const res = await axios.put(`${url}/api/documents/${id}/restore`);
        toast.success(res.data.message || "Document activated successfully");
        fetchDocuments();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to activate document");
      }
    }
  };

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If it already starts with http or /uploads, return as is
    if (imagePath.startsWith("http") || imagePath.startsWith("/uploads")) {
      return `${url}${imagePath}`;
    }
    // Otherwise, add /uploads/ prefix
    return `${url}/uploads/${imagePath}`;
  };

  const handleImageError = (docId, imageType) => {
    setImageError(prev => ({
      ...prev,
      [`${docId}-${imageType}`]: true
    }));
    console.error(`Failed to load ${imageType} for document ${docId}`);
  };

  if (loading) return <div className="loading">Loading documents...</div>;

  return (
    <div className="registration-container">
      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)}>
              <X size={24} />
            </button>
            <img src={selectedImage} alt="Document Preview" />
          </div>
        </div>
      )}

      <div className="header">
        <h2>Documents List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button
            onClick={() => navigate("/registration/new")}
            className="add-button"
          >
            New Registration
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="registration-table">
          <thead>
            <tr>
              <th>Document No</th>
              <th>Customer</th>
              <th>Document Type</th>
              <th>Photo</th>
              <th>Document Upload</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
              <th>Actions</th>
              </tr>
            </thead>
          <tbody>
            {documents.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No documents found
                 </td>
               </tr>
            ) : (
              documents.map((doc) => {
                const photoUrl = getFullImageUrl(doc.photos);
                const documentUrl = getFullImageUrl(doc.Documentupload);
                const photoError = imageError[`${doc._id}-photo`];
                const docError = imageError[`${doc._id}-document`];
                
                console.log(`Document ${doc.no} - Photo URL:`, photoUrl);
                console.log(`Document ${doc.no} - Document URL:`, documentUrl);
                
                return (
                  <tr key={doc._id} className={doc.status === 'inactive' ? 'inactive-row' : ''}>
                    <td className="doc-no">{doc.no}</td>
                    <td>
                      <div className="customer-info">
                        <User size={14} />
                        <span>{doc.customer?.fullname || 'N/A'}</span>
                      </div>
                    </td>
                    <td>
                      <span className="doc-type-badge">{doc.Document}</span>
                    </td>
                    <td className="image-cell">
                      {photoUrl && !photoError ? (
                        <div className="thumbnail-container">
                          <img 
                            src={photoUrl} 
                            alt="Photo" 
                            className="thumbnail"
                            onClick={() => setSelectedImage(photoUrl)}
                            onError={() => handleImageError(doc._id, 'photo')}
                          />
                          <button 
                            className="view-image-btn"
                            onClick={() => setSelectedImage(photoUrl)}
                            title="View Full Image"
                          >
                            <Eye size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="no-image">
                          <Image size={24} />
                          <span>No Photo</span>
                        </div>
                      )}
                    </td>
                    <td className="image-cell">
                      {documentUrl && !docError ? (
                        <div className="thumbnail-container">
                          <img 
                            src={documentUrl} 
                            alt="Document" 
                            className="thumbnail"
                            onClick={() => setSelectedImage(documentUrl)}
                            onError={() => handleImageError(doc._id, 'document')}
                          />
                          <button 
                            className="view-image-btn"
                            onClick={() => setSelectedImage(documentUrl)}
                            title="View Full Image"
                          >
                            <Eye size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="no-image">
                          <FileText size={24} />
                          <span>No Document</span>
                        </div>
                      )}
                    </td>
                    <td>{new Date(doc.issue_date).toLocaleDateString()}</td>
                    <td>{new Date(doc.expired_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${doc.status}`}>
                        {doc.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="actions">
                        {doc.status === "active" ? (
                          <>
                            <button
                              onClick={() => navigate(`/registration/customer/${doc.customer?._id}/documents`)}
                              className="action-btn view"
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleSoftDelete(doc._id)}
                              className="action-btn delete"
                              title="Deactivate"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleRestore(doc._id)}
                            className="restore-btn"
                            title="Activate"
                          >
                            <RotateCcw size={16} /> Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DocumentsList;